/**
 * Approved mechanical transport binding layer.
 * Separated from vendored agent core (gemini.agents.core.ts).
 * Source original: services/gemini.ts generateWithRetry + getAI replacement.
 */
import { GEMINI_MODEL_FLASH } from './constants';
import type { GoldenGenerateFn, GoldenGenerateParams, GoldenGenerateResponse } from './goldenTransport';
import { extractAndParseJSON } from './goldenJson';
import {
  MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_AGENT_FUNCTION,
  MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT,
} from '../adapters/originalCompatRetryPolicy';

export const GOLDEN_LOGICAL_MAX_RETRIES = 5;
export const GOLDEN_BASE_DELAY_MS = 3000;

/** Maximum upstream HTTP calls per one logical agent attempt (client transport cap). */
export const MAX_CLIENT_TRANSPORT_ATTEMPTS_PER_LOGICAL_CALL = 1;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

let _goldenGenerateFn: GoldenGenerateFn | null = null;

export function bindGoldenGenerateFn(fn: GoldenGenerateFn): void {
  _goldenGenerateFn = fn;
}

export function getBoundGoldenGenerateFn(): GoldenGenerateFn | null {
  return _goldenGenerateFn;
}

export function resetGoldenGenerateBinding(): void {
  _goldenGenerateFn = null;
}

export async function goldenGenerateWithRetry(
  params: GoldenGenerateParams,
  timeoutMs = 300000,
  audit?: {
    agent: string;
    logicalCallId: string;
    logicalAttempt: number;
    onAudit?: (entry: OriginalCompatRetryAuditEntry) => void;
  },
): Promise<GoldenGenerateResponse> {
  if (!_goldenGenerateFn) {
    throw new Error('[originalEngine] Golden generate transport is not bound.');
  }

  let lastError: unknown;

  if (!params.config) params.config = {};

  if (params.model === GEMINI_MODEL_FLASH && params.config.thinkingConfig) {
    delete params.config.thinkingConfig;
  }

  for (let i = 0; i < GOLDEN_LOGICAL_MAX_RETRIES; i++) {
    const logicalAttempt = i + 1;
    try {
      const response = await _goldenGenerateFn(params, timeoutMs);
      audit?.onAudit?.({
        agent: audit.agent,
        logicalCallId: audit.logicalCallId,
        originalLogicalAttempt: logicalAttempt,
        clientHttpAttempt: 1,
        serverUpstreamAttempt: 1,
        maximumServerUpstreamAttempts: MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_LOGICAL_ATTEMPT,
        maximumTotalUpstreamAttemptsForAgentFunction: MAXIMUM_UPSTREAM_MODEL_ATTEMPTS_PER_AGENT_FUNCTION,
      });
      if (params.config?.responseMimeType === 'application/json') {
        extractAndParseJSON(response.text || '');
      }
      return response;
    } catch (error: any) {
      lastError = error;
      const errorMsg = error.message?.toLowerCase() || String(error).toLowerCase();
      const errorStr = JSON.stringify(error).toLowerCase();
      const isRetryable =
        error?.status === 429 ||
        error?.status === 500 ||
        error?.status === 502 ||
        error?.status === 503 ||
        error?.code === 503 ||
        errorMsg.includes('failed to parse ai response as json') ||
        errorMsg.includes('empty response from ai') ||
        errorMsg.includes('timed out') ||
        errorMsg.includes('unavailable') ||
        errorMsg.includes('high demand') ||
        errorMsg.includes('503') ||
        errorStr.includes('503') ||
        errorStr.includes('unavailable') ||
        errorStr.includes('high demand');

      if (isRetryable && i < GOLDEN_LOGICAL_MAX_RETRIES - 1) {
        console.warn(`Retryable error caught (Attempt ${logicalAttempt}/${GOLDEN_LOGICAL_MAX_RETRIES}):`, error.message || error);
        await delay(GOLDEN_BASE_DELAY_MS * Math.pow(2, i));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

export interface OriginalCompatRetryAuditEntry {
  agent: string;
  logicalCallId: string;
  originalLogicalAttempt: number;
  clientHttpAttempt: number;
  serverUpstreamAttempt: number;
  maximumServerUpstreamAttempts: number;
  maximumTotalUpstreamAttemptsForAgentFunction: number;
}

export function logOriginalCompatRetryAudit(entry: OriginalCompatRetryAuditEntry): void {
  console.log(`[ORIGINAL COMPAT RETRY AUDIT] ${JSON.stringify(entry)}`);
}
